import { useRef, useCallback } from 'preact/hooks';
import { mergeInterpolations } from '../utils/UNSAFE_mergeInterpolations';
import { flexitemInterpolations } from '../utils/UNSAFE_interpolations/flexitem';
import type { FlexitemProps } from '../utils/UNSAFE_interpolations/flexitem';
import { getKey } from '../utils/PRIVATE_collectionUtils';
import { useReorderableItem } from '../hooks/PRIVATE_useReorderable';
import { ReorderableItemProps } from '../UNSAFE_Collection/Reorderable.types';
import { styles } from './themes/ReorderableTabBarItemStyles.css';
import { isKeyDefined } from '../utils/UNSAFE_keys';
import { useUser } from '../hooks/UNSAFE_useUser';
import { classNames } from '#utils/UNSAFE_classNames';

export const ITEM_SELECTOR = '[role="tab"]';

type ReorderableTabBarItem<K extends string | number> = ReorderableItemProps<K> & {
  layout: 'stretch' | 'condense' | undefined;
  isFirstItem?: boolean;
  isLastItem?: boolean;
};

/**
 * A private item that will be used by ReorderableTabBar, location TBD (could be moved to PRIVATE_TabBar)
 */
export function ReorderableTabBarItem<K extends string | number>({
  children,
  dragKey,
  setDragKey,
  onReorder,
  layout,
  rootRef,
  isFirstItem,
  isLastItem
}: ReorderableTabBarItem<K>) {
  const tabBarItemRef = useRef<HTMLDivElement | null>(null);
  const getItem = useCallback(
    () => tabBarItemRef?.current?.querySelector(ITEM_SELECTOR) as HTMLElement,
    []
  );
  const setTransferData = useCallback(
    (event: DragEvent) => {
      event.dataTransfer?.setData('text/tabbar-item-key', getKey(getItem()) + '');
    },
    [getItem]
  );

  const { direction } = useUser();
  const isRtl = direction === 'rtl';

  // Flex dimensions
  const styleInterpolations = mergeInterpolations<FlexitemProps>([
    ...Object.values(flexitemInterpolations)
  ]);
  const flexDimensions = styleInterpolations({
    flex: layout === 'stretch' ? '1 0 auto' : '0 0 auto'
  });

  const { rootProps, isDragged, overlayZoneBeforeProps, overlayZoneAfterProps } =
    useReorderableItem({
      isDisabled: onReorder ? false : true,
      dragKey,
      setDragKey,
      onReorder,
      rootRef,
      ref: tabBarItemRef,
      getItem,
      setTransferData,
      itemStyles: {
        itemOverlayFront: styles.itemOverlayFront,
        itemOverlayRear: styles.itemOverlayRear,
        itemDragger: styles.itemDragger
      },
      itemSelector: ITEM_SELECTOR
    });

  const overlayZoneBeforeClass = classNames([
    overlayZoneBeforeProps?.class,
    // Set the classes specific to isFirstItem/isLastItem which is passed down from reorderable tabbar which evaluated while mapping the children
    !isRtl && isFirstItem && styles.itemOverlayFrontFirst,
    isRtl && isLastItem && styles.itemOverlayFrontFirst
  ]);

  const overlayZoneAfterClass = classNames([
    overlayZoneAfterProps?.class,
    // Set the classes specific to isFirstItem/isLastItem which is passed down from reorderable tabbar which evaluated while mapping the children
    !isRtl && isLastItem && styles.itemOverlayRearLast,
    isRtl && isFirstItem && styles.itemOverlayRearLast
  ]);

  return (
    <div
      {...rootProps}
      draggable={onReorder ? true : false}
      data-oj-draggable={onReorder ? '' : undefined}
      style={flexDimensions}>
      {children}
      {!isDragged && isKeyDefined(dragKey) && (
        <>
          <div {...overlayZoneBeforeProps} class={overlayZoneBeforeClass}></div>
          <div {...overlayZoneAfterProps} class={overlayZoneAfterClass}></div>
        </>
      )}
    </div>
  );
}
